import { useEffect, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe, type StripeElementsOptionsClientSecret } from '@stripe/stripe-js';
import { v } from '../constants';
import {
  createHintPackIntent,
  createSetupIntent,
  createSubscriptionIntent,
  fetchStripeConfig,
} from '../lib/billing';

// White-label checkout — embeds Stripe's PaymentElement inside the
// hexponents.com window so the user never bounces to checkout.stripe.com.
// Fewer brand-jarring transitions and the checkout looks like part of
// the app.

export type CheckoutKind =
  | { kind: 'subscription'; cadence: 'monthly' | 'yearly' }
  | { kind: 'hint_pack' }
  | { kind: 'change_card' };  // SetupIntent — replaces saved card without charging

interface Props {
  product: CheckoutKind;
  onClose: () => void;
  /// Fired after a successful payment. Caller should refresh auth.user
  /// (premium status / hint balance) and dismiss the modal.
  onSuccess: () => void;
}

/// Top-level: fetches publishable key + intent client_secret, then
/// hands them off to <Elements> for the actual form. Loading / error
/// states live here so the inner form stays focused on the payment UX.
export function CheckoutModal({ product, onClose, onSuccess }: Props) {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [readinessKey, setReadinessKey] = useState(0); // forces a retry when the user clicks "Try again"

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      setClientSecret(null);
      const cfg = await fetchStripeConfig();
      if (cancelled) return;
      if (!cfg) { setError("Couldn't load checkout. Please try again."); return; }
      setStripePromise(loadStripe(cfg.publishableKey));

      const intentResult = product.kind === 'subscription'
        ? await createSubscriptionIntent(product.cadence)
        : product.kind === 'hint_pack'
          ? await createHintPackIntent()
          : await createSetupIntent();
      if (cancelled) return;
      if (!intentResult.ok) {
        setError(humanizeIntentError(intentResult.error));
        return;
      }
      setClientSecret(intentResult.intent.clientSecret);
    })();
    return () => { cancelled = true; };
  }, [product, readinessKey]);

  // Stripe Elements injects styles INTO an iframe (the card-number /
  // CVC / expiry inputs are cross-origin iframes for PCI scope). CSS
  // variables defined on our <html> don't propagate into that iframe,
  // so we have to resolve `var(--paper)` etc. to the actual hex/rgb
  // values at render time and pass those resolved strings to Stripe.
  //
  // Read once per mount via this small helper. Re-runs whenever the
  // theme effectively toggles (we hash the resolved palette into
  // `themeKey` so the useMemo below regenerates the appearance + the
  // user toggling dark/light rebuilds the form).
  const resolvePalette = () => {
    const cs = getComputedStyle(document.documentElement);
    const get = (name: string): string => cs.getPropertyValue(`--${name}`).trim() || '#000';
    return {
      paper:     get('paper'),
      paperDeep: get('paperDeep'),
      ink:       get('ink'),
      inkSoft:   get('inkSoft'),
      inkMute:   get('inkMute'),
      rule:      get('rule'),
      accent:    get('accent'),
      bad:       get('bad'),
    };
  };
  const palette = useMemo(resolvePalette, [readinessKey]);

  const options: StripeElementsOptionsClientSecret | undefined = useMemo(() => {
    if (!clientSecret) return undefined;
    return {
      clientSecret,
      // Theme override so the form picks up our paper/ink palette
      // instead of Stripe's default light grey. We have to set the
      // secondary/placeholder/icon colors explicitly — Stripe's
      // auto-derivation from colorBackground produces low-contrast
      // greys against our dark-mode paper (#1a1612) which made the
      // input labels + placeholders nearly unreadable. Each variable
      // is mapped to an existing CSS var so the form re-themes with
      // the rest of the app when the user toggles dark/light.
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: palette.accent,
          colorBackground: palette.paper,
          colorText: palette.ink,
          // Labels above each field ("Card number", "Expiration"…)
          colorTextSecondary: palette.inkSoft,
          // Placeholder text inside fields ("1234 1234 1234 1234")
          colorTextPlaceholder: palette.inkMute,
          // Field icons (card brand, CVC ?, chevron, redirect)
          colorIcon: palette.inkSoft,
          colorIconHover: palette.ink,
          colorIconChevronDown: palette.inkSoft,
          colorIconChevronDownHover: palette.ink,
          colorIconRedirect: palette.inkSoft,
          colorDanger: palette.bad,
          fontFamily: 'Inter Tight, system-ui, sans-serif',
          spacingUnit: '4px',
          borderRadius: '8px',
        },
        // Per-element selector overrides for things `variables` can't
        // reach. The input fields themselves get a slightly-deeper
        // background so they're visible against the modal paper, and
        // the border picks up our rule color so it doesn't look like
        // a floating text block.
        rules: {
          '.Input': {
            backgroundColor: palette.paperDeep,
            border: `1px solid ${palette.rule}`,
            color: palette.ink,
          },
          '.Input:focus': {
            borderColor: palette.accent,
            boxShadow: `0 0 0 2px ${palette.accent}33`,
          },
          '.Input--invalid': {
            borderColor: palette.bad,
          },
          '.Label': {
            color: palette.inkSoft,
            fontWeight: '500',
          },
          '.Tab': {
            backgroundColor: palette.paperDeep,
            border: `1px solid ${palette.rule}`,
            color: palette.inkSoft,
          },
          '.Tab:hover': {
            color: palette.ink,
          },
          '.Tab--selected': {
            backgroundColor: palette.paper,
            borderColor: palette.ink,
            color: palette.ink,
          },
        },
      },
    };
  }, [clientSecret, palette]);

  return (
    <ModalShell title={titleFor(product)} onClose={onClose}>
      {error ? (
        <div style={{ padding: 12 }}>
          <div style={{ color: v('bad'), marginBottom: 12 }}>{error}</div>
          <button className="hm-action" onClick={() => setReadinessKey(k => k + 1)}>Try again</button>
        </div>
      ) : !stripePromise || !clientSecret || !options ? (
        <div style={{ padding: 24, textAlign: 'center', color: v('inkSoft') }}>Loading checkout…</div>
      ) : (
        <Elements stripe={stripePromise} options={options}>
          <CheckoutForm product={product} onSuccess={onSuccess} />
        </Elements>
      )}
    </ModalShell>
  );
}

function titleFor(p: CheckoutKind): string {
  if (p.kind === 'hint_pack') return 'Buy 25 hints — $0.99';
  if (p.kind === 'change_card') return 'Update payment method';
  return p.cadence === 'yearly' ? 'Hexponents Premium — $19.99/year' : 'Hexponents Premium — $1.99/month';
}

function humanizeIntentError(code: string): string {
  switch (code) {
    case 'already_subscribed': return "You're already subscribed. Manage your subscription from the Account menu.";
    case 'stripe_not_configured':
    case 'cadence_not_configured':
    case 'hint_pack_not_configured':
      return 'Payments are not yet available. Please try again later.';
    case 'no_client_secret': return "Stripe didn't return a usable session. Please try again.";
    default: return "Couldn't start checkout. Please try again.";
  }
}

// MARK: – Inner form (rendered inside <Elements>)

function CheckoutForm({ product, onSuccess }: { product: CheckoutKind; onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setFormError(null);
    // confirmSetup for the change-card flow; confirmPayment for any
    // actual charge (subscription + hint pack). Both return the same
    // shape — { error, ... } — so the post-call branch is identical.
    const result = product.kind === 'change_card'
      ? await stripe.confirmSetup({ elements, redirect: 'if_required' })
      : await stripe.confirmPayment({ elements, redirect: 'if_required' });
    if (result.error) {
      setFormError(result.error.message ?? 'Something went wrong. Please try again.');
      setSubmitting(false);
      return;
    }
    // Success path — webhook will land on the server and credit the
    // entitlement; the caller polls /me to flip the UI.
    onSuccess();
  };

  const ctaLabel = (() => {
    if (product.kind === 'hint_pack') return 'Buy 25 hints — $0.99';
    if (product.kind === 'change_card') return 'Save payment method';
    return product.cadence === 'yearly' ? 'Subscribe yearly — $19.99' : 'Subscribe monthly — $1.99';
  })();

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />
      {formError && (
        <div style={{ color: v('bad'), marginTop: 12, fontSize: 13 }}>{formError}</div>
      )}
      <button
        type="submit"
        disabled={!stripe || !elements || submitting}
        className="hm-action"
        style={{
          marginTop: 16,
          width: '100%',
          padding: '12px 16px',
          background: v('ink'),
          color: v('paper'),
          border: 'none',
          borderRadius: 12,
          cursor: submitting ? 'wait' : 'pointer',
          fontWeight: 600,
          fontSize: 15,
          opacity: submitting ? 0.7 : 1,
        }}
      >
        {submitting ? 'Processing…' : ctaLabel}
      </button>
      {product.kind !== 'hint_pack' && (
        <div style={{ fontSize: 11, color: v('inkMute'), marginTop: 12, lineHeight: 1.4 }}>
          Subscriptions auto-renew until canceled. You can cancel anytime from the Account menu.
        </div>
      )}
    </form>
  );
}

// MARK: – Modal shell (portal + overlay)

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  // Lock body scroll while the modal is open so the page underneath
  // doesn't scroll when the user wheels inside the form.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: v('paper'),
          color: v('ink'),
          border: `1px solid ${v('rule')}`,
          borderRadius: 18,
          padding: 24,
          maxWidth: 460, width: '100%',
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{title}</div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent', border: 'none', color: v('inkMute'),
              cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: 4,
            }}
          >×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
